/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import { v4 as uuidv4 } from 'uuid';

import { IKnmFormSubject } from 'KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';
import { createKnmReasonExtendedSelectOption } from 'CorePackage/CatalogsModule/common/createKnmReasonTypeSelectOption';
import { IKnmReason } from 'APIPackage/KnmModule/interfaces/IKnmReason';
import { IKnmReasonType } from 'APIPackage/CatalogsModule/interfaces/IKnmReasonType';
import { convertSubjectDataToFormValues } from 'KnmPackage/KnmSubjectModule/common/convertSubjectDataToformValues';
import { IDictionaryKnoOrganizationSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnoOrganizationSelectOption';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';

import { IKnmPmFormReason } from '../interfaces/IKnmPmFormReason';
import { IKnmPmForm } from '../interfaces/IKnmPmForm';

interface IRestorePmInitialValuesParams {
  savedValues: Record<string, any>;
  savedValuesPmErknm: Record<string, any>;
  formRef: IKnmFormRef;
  values: IKnmPmForm;
  knoOrganizationPm: IDictionaryKnoOrganizationSelectOption | undefined;
  startDate: string | undefined;
  stopDate: string | undefined;
  markFieldAsRestored: (fieldName: string) => void;
  cloneReasons: (reasons: IKnmPmFormReason[]) => IKnmPmFormReason[];
  setRestoredReasonsPm: (reasons: IKnmPmFormReason[] | undefined) => void;
  restoredReasonsPmRef: React.MutableRefObject<IKnmPmFormReason[] | undefined>;
  isRestoringRef: React.MutableRefObject<boolean>;
}

interface IRestoreResult {
  hasRestored: boolean;
}

interface IFieldMapping {
  key: string;
  altKey?: string;
  defaultValue?: any;
}

const normalizeReasonsPm = (
  savedReasonsPm: any[],
  cloneReasons: (reasons: IKnmPmFormReason[]) => IKnmPmFormReason[],
  setRestoredReasonsPm: (reasons: IKnmPmFormReason[] | undefined) => void,
  restoredReasonsPmRef: React.MutableRefObject<IKnmPmFormReason[] | undefined>,
  markFieldAsRestored: (fieldName: string) => void,
): { normalizedReasons: IKnmPmFormReason[]; clonedReasons: IKnmPmFormReason[] } => {
  const normalizedReasons: IKnmPmFormReason[] = savedReasonsPm.map((reason: any) => {
    const listId = reason.listId || uuidv4();

    let normalizedType = reason.type ? { ...reason.type } : undefined;

    if (!normalizedType && reason.reason?.type) {
      normalizedType = createKnmReasonExtendedSelectOption(
        reason.reason as IKnmReason,
        reason.reason.type as IKnmReasonType,
        reason.reason.flagProfvizit,
        reason.reason.inspectionResults,
      );
    }

    if (normalizedType && !normalizedType.label) {
      normalizedType.label =
        normalizedType.shortTitle ??
        normalizedType.title ??
        reason.reason?.shortTitle ??
        reason.reason?.title ??
        normalizedType.name ??
        '';
    }

    if (normalizedType && !normalizedType.value) {
      const fallbackValue =
        normalizedType.code ?? reason.reason?.type?.code ?? reason.reason?.ervkUuid ?? normalizedType.id;

      normalizedType.value = typeof fallbackValue === 'string' ? fallbackValue : `${fallbackValue ?? ''}`;
    }

    return {
      ...reason,
      listId,
      type: normalizedType,
    };
  });

  const cloned = cloneReasons(normalizedReasons);

  restoredReasonsPmRef.current = cloned;
  setRestoredReasonsPm(cloned);
  markFieldAsRestored('reasonsPm');

  return { normalizedReasons, clonedReasons: cloned };
};

export const restorePmInitialValues = ({
  savedValues,
  savedValuesPmErknm,
  formRef,
  values,
  knoOrganizationPm,
  startDate,
  stopDate,
  markFieldAsRestored,
  cloneReasons,
  setRestoredReasonsPm,
  restoredReasonsPmRef,
  isRestoringRef,
}: IRestorePmInitialValuesParams): IRestoreResult => {
  const form = formRef.current;

  if (!form) {
    return { hasRestored: false };
  }

  try {
    const currentFormValues = form.getState().values || {};
    const initialValues: Record<string, any> = { ...values, ...currentFormValues };

    const fieldMappings: IFieldMapping[] = [
      { key: 'knoOrganizationPm', defaultValue: knoOrganizationPm },
      { key: 'prosecutorOrganizationErknm' },
      { key: 'supervisionTypePm' },
      { key: 'pmType' },
      { key: 'objectsPm' },
      { key: 'inspectorsErknm' },
      { key: 'experts' },
      { key: 'location' },
      { key: 'fiasRegion' },
      { key: 'region' },
      { key: 'notInFIAS' },
      { key: 'manualAddress' },
      { key: 'videoCallInfo' },
      { key: 'requirementsNpa' },
      { key: 'prosecutorOrganization' },
      { key: 'interactionCategory' },
      { key: 'startDate', defaultValue: startDate },
      { key: 'stopDate', defaultValue: stopDate },
      { key: 'isHasCollaboratingOrganization' },
      { key: 'collaboratingOrganizationsErknm' },
      { key: 'docTarget' },
    ];

    fieldMappings.forEach(({ key, altKey, defaultValue }) => {
      if (savedValues[key]) {
        initialValues[key] = savedValues[key];
        markFieldAsRestored(key);
      } else if (altKey && savedValues[altKey]) {
        initialValues[key] = savedValues[altKey];
        markFieldAsRestored(key);
      } else if (defaultValue) {
        initialValues[key] = defaultValue;
      }
    });

    if (savedValues.reasonsPm && Array.isArray(savedValues.reasonsPm) && savedValues.reasonsPm.length > 0) {
      const { normalizedReasons } = normalizeReasonsPm(
        savedValues.reasonsPm,
        cloneReasons,
        setRestoredReasonsPm,
        restoredReasonsPmRef,
        markFieldAsRestored,
      );

      initialValues.reasonsPm = normalizedReasons;
    }

    if (savedValuesPmErknm?.subject) {
      const convertedSubject = convertSubjectDataToFormValues(savedValuesPmErknm.subject);

      (Object.keys(convertedSubject) as Array<keyof IKnmFormSubject>).forEach(key => {
        initialValues[key] = convertedSubject[key];
        markFieldAsRestored(key);
      });
    }

    isRestoringRef.current = true;
    form.initialize(initialValues);
    isRestoringRef.current = false;

    const additionalFields = [
      'eventsPm',
      'inspectorsErknm',
      'experts',
      'videoCallInfo',
      'requirementsNpa',
      'organizationName',
      'isUnableToRefuse',
      'ogrn',
    ];

    additionalFields.forEach(field => {
      const value = savedValues[field] ?? initialValues[field];

      if (value) {
        form.change(field as keyof IKnmPmForm, value);
        markFieldAsRestored(field);
      }
    });

    return { hasRestored: true };
  } catch (e) {
    console.error('Error restoring initial values:', e);

    return { hasRestored: false };
  }
};
